import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, TextField } from '@mui/material';
import {
  activeMeasurementFieldId,
  measurementsByField,
  measurementToolState,
} from '@/components/Analysis/modules/measurementTool/state';
import {
  InputAdormentIconTypeEnum,
  DisplayConfig,
  Maybe,
  UnitSystemEnum,
  UnitsEnum,
} from '@/__generated__/graphql';
import { useInputAdornment } from '@/hooks';
import * as state from '@/components/Analysis/state';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import queryString from 'query-string';
import { convertFeetToMeters, localiseMeasurement } from '@/utils/unitSystem';
import { MeasurementToolState } from '@/types';
import { calcMeasurementLinesDistance } from '@/components/Analysis/modules/measurementTool/hooks/utils';
import { useAtomValue, useSetAtom } from 'jotai';

type Props = {
  title?: string;
  displayConfig?: Maybe<DisplayConfig>;
  onFieldValueChange?: (value: string) => void;
  inputAdormantHandlerMap?: {
    [InputAdormentIconTypeEnum.MeasurementTool]?: () => void;
    [InputAdormentIconTypeEnum.Reload]?: () => void;
    ''?: () => void;
  };
  value?: string;
  templateFieldId?: string;
};

const emptyHandler = () => {};

export const EditableProposedPaintLength = ({
  rows,
  RenderInputAdormant,
  onFieldValueChange,
  title,
  value,
}: {
  rows: number;
  RenderInputAdormant: JSX.Element;
  onFieldValueChange?: (value: string) => void;
  title: string | undefined;
  value?: string;
}) => {
  const unitSystem = useAtomValue(unitSystemState);

  const paintLength =
    localiseMeasurement({
      unitSystem,
      sourceUnit: UnitsEnum.Meter,
      value: Number(value),
      roundToDecimal: 2,
      showUnitLabel: false,
    }) ?? 0;

  return (
    <TextField
      id={`TextArea-${title}`}
      label={`${title} (${unitSystem === UnitSystemEnum.Imperial ? 'ft' : 'm'})`}
      multiline={false}
      rows={rows}
      defaultValue={isNaN(Number(paintLength)) ? 0 : paintLength}
      onChange={(event) =>
        onFieldValueChange &&
        onFieldValueChange(
          localiseMeasurement({
            unitSystem,
            value:
              unitSystem === UnitSystemEnum.Imperial
                ? convertFeetToMeters(Number(event.target.value))
                : Number(event.target.value),
            showUnitLabel: false,
            roundToDecimal: 2,
          })
        )
      }
      slotProps={{
        input: {
          endAdornment: RenderInputAdormant,
        },
      }}
    />
  );
};

export const AutoGeneratedPaintLength = ({
  rows,
  RenderInputAdormant,
  onFieldValueChange,
  title,
  templateFieldId,
}: {
  rows: number;
  RenderInputAdormant: JSX.Element;
  onFieldValueChange?: (value: string) => void;
  title: string | undefined;
  templateFieldId: string;
}) => {
  const unitSystem = useAtomValue(unitSystemState);
  const setActiveFieldId = useSetAtom(activeMeasurementFieldId);
  const setMeasurementToolState = useSetAtom(measurementToolState);
  const allMeasurements = useAtomValue(measurementsByField);

  const fieldMeasurement = allMeasurements?.[templateFieldId];

  const fieldDistance = useMemo(() => {
    const points = fieldMeasurement?.points;

    if (!Array.isArray(points) || points.length < 2) {
      return '';
    }
    return calcMeasurementLinesDistance(points).toFixed(2);
  }, [fieldMeasurement]);

  const computedPaintLengthValue = useMemo(() => {
    if (!fieldDistance) return '';

    const numericLength =
      localiseMeasurement({
        unitSystem,
        sourceUnit: UnitsEnum.Meter,
        value: Number(fieldDistance),
        roundToDecimal: 2,
        showUnitLabel: false
      }) ?? 0;

    return numericLength;
  }, [fieldDistance, unitSystem]);

  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (onFieldValueChange && fieldDistance && lastSentRef.current !== fieldDistance) {
      lastSentRef.current = fieldDistance;
      onFieldValueChange(fieldDistance);
    }
  }, [fieldDistance, onFieldValueChange]);

  const handleClickAdornment = useCallback(() => {
    setActiveFieldId(templateFieldId);
    setMeasurementToolState(MeasurementToolState.WAITING_FOR_POINT);
  }, [templateFieldId, setActiveFieldId, setMeasurementToolState]);

  return (
    <TextField
      id={`TextArea-${title}`}
      label={`${title} (${unitSystem === UnitSystemEnum.Imperial ? 'ft' : 'm'})`}
      multiline={false}
      rows={rows}
      value={computedPaintLengthValue}
      onChange={emptyHandler}
      slotProps={{
        input: {
          endAdornment: (
            <button
              type="button"
              onClick={handleClickAdornment}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              {RenderInputAdormant}
            </button>
          ),
        },
      }}
    />
  );
};

export const ProposedPaintLength = ({
  title,
  displayConfig,
  onFieldValueChange,
  inputAdormantHandlerMap,
  value,
  templateFieldId = '',
}: Props) => {
  const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });
  const insightsTab = parsed.tab;

  const rows = displayConfig?.textAreaRows ?? 4;
  const inputAdorment = displayConfig?.inputAdorment || undefined;
  const iconType = displayConfig?.inputAdorment?.iconType ?? '';

  const selectedSpherical = useAtomValue(state.selectedSpherical);

  const RenderInputAdormant: JSX.Element = useInputAdornment({
    inputAdorment,
    iconType,
    inputAdormantHandlerMap,
    displayConfig,
    selectedSpherical,
  });

  return (
    <Box
      sx={{
        width: '100%',
        '& .MuiTextField-root': { width: '100%' },
      }}
      component="form"
      noValidate
      autoComplete="off"
    >
      <div>
        {insightsTab ? (
          <EditableProposedPaintLength
            rows={rows}
            RenderInputAdormant={RenderInputAdormant}
            onFieldValueChange={onFieldValueChange}
            title={title}
            value={value}
          />
        ) : (
          <AutoGeneratedPaintLength
            rows={rows}
            RenderInputAdormant={RenderInputAdormant}
            onFieldValueChange={onFieldValueChange}
            title={title}
            templateFieldId={templateFieldId}
          />
        )}
      </div>
    </Box>
  );
};
